import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { PlusIcon, EditIcon, TrashIcon, PackageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierManagementSettings = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supplierData, setSupplierData] = useState({ name: '', country: '' });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  useEffect(() => {
    fetchSuppliers();
    fetchCountries();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers`);
      const suppliersData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('İşletmeler yüklenirken hata:', error);
      toast.error('İşletmeler yüklenirken hata oluştu');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      const countriesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setCountries(countriesData);
    } catch (error) {
      console.error('Ülkeler yüklenirken hata:', error);
      toast.error('Ülkeler yüklenirken hata oluştu');
      setCountries([]);
    }
  };

  const handleSaveSupplier = async () => {
    if (!supplierData.name || !supplierData.name.trim()) {
      toast.error('İşletme adı gerekli');
      return;
    }

    if (!supplierData.country || !supplierData.country.trim()) {
      toast.error('Ülke seçimi gerekli');
      return;
    }

    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, supplierData);
        toast.success('İşletme güncellendi');
      } else {
        await axios.post(`${API}/suppliers`, supplierData);
        toast.success('İşletme oluşturuldu');
      }

      setDialogOpen(false);
      setSupplierData({ name: '', country: '' });
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'İşletme kaydedilirken hata oluştu';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'İşletme kaydedilirken hata oluştu');
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`"${supplier.name}" işletmesini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/suppliers/${supplier.id}`);
      toast.success('İşletme silindi');
      fetchSuppliers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'İşletme silinirken hata oluştu';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'İşletme silinirken hata oluştu');
    }
  };

  const openDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierData({
        name: supplier.name,
        country: supplier.country
      });
    } else {
      setEditingSupplier(null);
      setSupplierData({ name: '', country: '' });
    }
    setCountrySearch('');
    setDialogOpen(true);
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-3 sm:p-6">
      <Card className="p-4 sm:p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <div className="flex items-center gap-2">
              <PackageIcon className="h-6 w-6 text-teal-600" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">İşletme Yönetimi</h2>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => openDialog()} 
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Yeni İşletme Ekle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingSupplier ? 'İşletme Güncelle' : 'Yeni İşletme'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="supplierName">İşletme Adı *</Label>
                    <Input
                      id="supplierName"
                      value={supplierData.name}
                      onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
                      placeholder="İşletme adını girin"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="supplierCountry">Ülke *</Label>
                    <Select
                      value={supplierData.country}
                      onValueChange={(value) => setSupplierData({ ...supplierData, country: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="settings-supplier-country-trigger">
                        <SelectValue placeholder="Ülke seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1.5 sticky top-0 bg-white border-b border-gray-100 z-10">
                          <Input
                            placeholder="Ülke ara..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            onKeyDown={(e) => {
                              // Prevent Radix typeahead from hijacking keystrokes
                              e.stopPropagation();
                            }}
                            className="h-8"
                            data-testid="settings-supplier-country-search"
                          />
                        </div>
                        {countries
                          .filter((c) =>
                            !countrySearch ||
                            c.name?.toLowerCase().includes(countrySearch.toLowerCase()) ||
                            c.code?.toLowerCase().includes(countrySearch.toLowerCase())
                          )
                          .map((country) => (
                            <SelectItem
                              key={country.code || country.id}
                              value={country.code || country.id}
                            >
                              {country.name}
                            </SelectItem>
                          ))}
                        {countrySearch &&
                          countries.filter((c) =>
                            c.name?.toLowerCase().includes(countrySearch.toLowerCase()) ||
                            c.code?.toLowerCase().includes(countrySearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              Sonuç bulunamadı
                            </div>
                          )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setDialogOpen(false)}
                    >
                      İptal
                    </Button>
                    <Button 
                      onClick={handleSaveSupplier}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {editingSupplier ? 'Güncelle' : 'Oluştur'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mb-4">
            <Input
              placeholder="İşletme ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <PackageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz işletme eklenmemiş'}
            </p>
            <p className="text-gray-400 mt-2">
              {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Başlamak için "Yeni İşletme Ekle" butonuna tıklayın'}
            </p>
          </div>
        ) : (
          <>
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100" data-testid="supplier-settings-mobile-list">
            {filteredSuppliers.map((supplier) => (
              <div key={supplier.id} className="p-4" data-testid={`supplier-setting-card-${supplier.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <PackageIcon className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      <span className="font-semibold text-gray-900 break-words">{supplier.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{supplier.country}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openDialog(supplier)}
                    className="flex-1 text-teal-600 hover:text-teal-700 hover:border-teal-600">
                    <EditIcon className="h-3.5 w-3.5 mr-1" /> Düzenle
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteSupplier(supplier)}
                    className="flex-1 text-red-600 hover:text-red-700 hover:border-red-600">
                    <TrashIcon className="h-3.5 w-3.5 mr-1" /> Sil
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşletme Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ülke
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <PackageIcon className="h-5 w-5 text-teal-600 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {supplier.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {supplier.country}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDialog(supplier)}
                          className="text-teal-600 hover:text-teal-700 hover:border-teal-600"
                        >
                          <EditIcon className="h-4 w-4 mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteSupplier(supplier)}
                          className="text-red-600 hover:text-red-700 hover:border-red-600"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Sil
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SupplierManagementSettings;
